import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface MetricHistoryPoint {
  ts: number;
  value: number;
  source: string;
}

export interface MetricHistoryData {
  tvl: MetricHistoryPoint[];
  fees: MetricHistoryPoint[];
  revenue: MetricHistoryPoint[];
  coverage: {
    days: number;
    tvl: number;
    fees: number;
    revenue: number;
    records: number;
  };
  metadata: {
    slug: string;
    app_name: string;
    app_symbol?: string;
    chain?: string;
    generated_at: string;
    source: string;
  };
}

export function useMetricHistory(slug: string | null, days: number = 90) {
  return useQuery({
    queryKey: ['metric-history', slug, days],
    queryFn: async (): Promise<MetricHistoryData | null> => {
      if (!slug) return null;

      const { data, error } = await supabase.functions.invoke('metric-history', {
        body: { slug, days }
      });

      if (error) {
        console.error('Error fetching metric history:', error);
        throw error;
      }

      return data;
    },
    enabled: !!slug,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

// Hook to get TVL change over a period
export function useTvlChange(slug: string | null, days: number = 7) {
  const { data, isLoading, error } = useMetricHistory(slug, days + 7);

  const tvlChange = (() => {
    if (!data?.tvl || data.tvl.length < 2) return null;

    const sorted = [...data.tvl].sort((a, b) => a.ts - b.ts);
    const cutoffTs = Date.now() - (days * 24 * 60 * 60 * 1000);
    
    // Find values around the cutoff date
    const recentValues = sorted.filter(p => p.ts >= cutoffTs);
    const olderValues = sorted.filter(p => p.ts < cutoffTs);

    if (recentValues.length === 0 || olderValues.length === 0) return null;

    const currentTvl = recentValues[recentValues.length - 1].value;
    const previousTvl = olderValues[olderValues.length - 1].value;

    if (previousTvl === 0) return null;

    return ((currentTvl - previousTvl) / previousTvl) * 100;
  })();

  return {
    tvlChange,
    isLoading,
    error
  };
}
